import React from 'react';
import { IconButton, InputAdornment, OutlinedInput } from '@mui/material';

import { type FiltersType } from '../hooks/useFilters';
import useSettings from '../hooks/useSettings';
import { formatSearchString } from '../Utils';
import Icon from '../Icon';

interface SearchProps {
	module: string;
	filters: FiltersType['filters'];
	handleFilterChange: FiltersType['handleFilterChange'];
	handleFiltersApply: FiltersType['handleFiltersApply'];
}

export function BaseSearch({ value, handleSubmit }: { value?: string; handleSubmit: (newValue?: string) => void }) {
	const { t } = useSettings();

	const inputRef = React.useRef<HTMLInputElement>(undefined);

	function handleSearchClick() {
		handleSubmit(formatSearchString(inputRef.current?.value));
	}

	function handleClearClick() {
		handleSubmit();
	}

	function handleKeyDown(event) {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleSearchClick();
		}
	}

	React.useEffect(() => {
		if (inputRef.current && (!value || value === '')) inputRef.current.value = '';
	}, [value]);

	return (
		<OutlinedInput
			className="p-0 pr-4"
			inputProps={{ ref: inputRef }}
			size="small"
			fullWidth
			placeholder={t('SEARCH_PLACEHOLDER')}
			defaultValue={value}
			endAdornment={
				<InputAdornment position="end">
					<IconButton size="small" onClick={handleClearClick}>
						<Icon icon="close" size={20} />
					</IconButton>
					<IconButton size="small" onClick={handleSearchClick}>
						<Icon icon="magnify" size={20} />
					</IconButton>
				</InputAdornment>
			}
			onKeyDown={handleKeyDown}
		/>
	);
}

function Search({ module, filters, handleFilterChange, handleFiltersApply }: SearchProps) {
	const { settings } = useSettings(module);

	const searchField = `${settings.modu_short}_search`;
	const searchFilter = filters.find(filt => filt.filt_field === searchField);
	const value = searchFilter?.filt_value;

	function handleSubmit(newValue?: string) {
		handleFilterChange(searchField, 'like', newValue || null);
		handleFiltersApply();
	}

	return <BaseSearch value={value !== null ? value?.toString() : undefined} handleSubmit={handleSubmit} />;
}

export default React.memo(Search);
